import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

import { FilterMatchMode } from 'primereact/api';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import { Column } from 'primereact/column';
import { DataTable, DataTableFilterMetaData } from 'primereact/datatable';
import { Dialog } from 'primereact/dialog';
import { useClickOutside } from 'primereact/hooks';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { InputText } from 'primereact/inputtext';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Toast } from 'primereact/toast';
import { Tooltip } from 'primereact/tooltip';
import {
  Tree,
  TreeCheckboxSelectionKeys,
  TreeMultipleSelectionKeys
} from 'primereact/tree';

import { useDebounce } from '../../hooks';
import { DropdownObjectType, TreeNode } from '../../types/interfaces';
import './styles/CustomDropdown.css';
import { extractSelectedNodes } from '../../utils';
import { transformToSelectionKeys } from '../../utils/transformToSelectionKeys';
import { withDialogFooterSpacing } from '../shared/dialogFooterSpacing';

function getComparableOptionId<
  T extends { id?: string | number; key?: string | number }
>(item: T): string | null {
  const r = item as Record<string, unknown>;
  const v = r.key ?? r.id;
  if (v === undefined || v === null || v === '') return null;
  return String(v);
}

export interface CustomDropdownProps<
  T extends { id?: string | number; key?: string | number } = DropdownObjectType
> {
  assetOptions: T[];
  placeholder?: string;
  fixedLabel?: string;
  multiplySelectMode?: boolean;
  columns?: {
    field: keyof T;
    header: string;
    width?: string;
    body?: (row: T) => React.ReactNode;
  }[];
  fieldName?: string;
  handleCreateClick?: () => void;
  selectedAsset: T | T[] | null;
  setSelectedAsset?: React.Dispatch<React.SetStateAction<T | T[] | null>>;
  onSelectionChange?: (selected: T | T[] | null) => void;
  onTreeSelectionChange?: (department: TreeNode | TreeNode[]) => void;
  useTree?: boolean;
  treeValue?: TreeNode[];
  passedClassName?: string;
  overlayClassName?: string;
  emptyMessage?: string;
  overlayWidthMultiplier?: number;
  minWidth?: number;
  customHeight?: number;
  disabled?: boolean; // Add the disabled prop
  showClearOnHover?: boolean;
  showToggleButton?: boolean;
  onToggleShowAll?: () => void;
  showAll?: boolean;
  allowDeselect?: boolean;
  selectAllBtn?: boolean;
  // TODO временный пропс?
  selectAllFromFiltered?: boolean;
  dialogCancelationWarn?: boolean;
  clearButtonTooltip?: string;
  createButtonTooltip?: string;
  selectAllTooltip?: string;
  toggleShowAllTooltip?: string;
  toggleShowFilteredTooltip?: string;
  filterWithLabel?: boolean;
  style?: React.CSSProperties;
  showDeletedWarning?: boolean;
  deletedWarningTooltip?: string;
  deletedWarningCheckTreeValue?: TreeNode[];
  deletedWarningCheckAssetOptions?: T[];
  departmentDropdown?: boolean;
  assetId?: number;
  setIsDepartmentDeleted?: (deleted: boolean) => void;
}

export const CustomDropdown = <
  T extends { id?: string | number; key?: string | number } = DropdownObjectType
>({
  assetOptions,
  handleCreateClick,
  placeholder,
  fixedLabel,
  multiplySelectMode = false,
  columns,
  fieldName = 'label',
  selectedAsset,
  setSelectedAsset,
  onSelectionChange,
  onTreeSelectionChange,
  useTree = false,
  treeValue = [],
  passedClassName,
  overlayClassName,
  emptyMessage,
  overlayWidthMultiplier,
  minWidth,
  customHeight,
  disabled = false,
  showClearOnHover = false,
  showToggleButton,
  onToggleShowAll,
  showAll,
  allowDeselect = false,
  selectAllBtn = false,
  selectAllFromFiltered = false,
  dialogCancelationWarn = false,
  clearButtonTooltip = 'Сбросить значение',
  createButtonTooltip = 'Создать новый элемент',
  selectAllTooltip,
  toggleShowAllTooltip = 'Показать все элементы',
  toggleShowFilteredTooltip = 'Показать отфильтрованные элементы',
  filterWithLabel = false,
  style,
  showDeletedWarning = false,
  deletedWarningTooltip,
  deletedWarningCheckTreeValue,
  deletedWarningCheckAssetOptions,
  departmentDropdown,
  assetId,
  setIsDepartmentDeleted
}: CustomDropdownProps<T>) => {
  const initialFilters = {
    global: { value: null, matchMode: FilterMatchMode.CONTAINS }
  };

  const [filters, setFilters] =
    useState<Record<string, DataTableFilterMetaData>>(initialFilters);
  const [selectedKey, setSelectedKey] = useState<
    string | TreeMultipleSelectionKeys | TreeCheckboxSelectionKeys | null
  >(null);
  const [metaKey] = useState<boolean>(false);
  const [cancelationWarnVisible, setCancelationWarnVisible] =
    useState<boolean>(false);
  const [isPanelVisible, setIsPanelVisible] = useState(false);
  const [treeExpandedKeys, setTreeExpandedKeys] = useState<
    Record<string, boolean>
  >({});
  const [selectAll, setSelectAll] = useState<boolean>(
    multiplySelectMode &&
      Array.isArray(selectedAsset) &&
      selectedAsset?.length === assetOptions?.length
  );

  const op = useRef<OverlayPanel>(null);
  const overlayElRef = useRef<HTMLElement>(null);
  const firstButtonRef = useRef<HTMLButtonElement>(null);
  const globalFilterRef = useRef<HTMLInputElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const dataKeyName = assetOptions?.every((item) => item.key !== undefined)
    ? 'key'
    : 'id';

  // selection logic
  const isMatch = useCallback((selected: T, option: T): boolean => {
    const a = getComparableOptionId(selected);
    const b = getComparableOptionId(option);
    return a !== null && b !== null && a === b;
  }, []);

  const selectedTableItems = Array.isArray(selectedAsset)
    ? assetOptions.filter((option) =>
        selectedAsset.some((selected) => isMatch(selected, option))
      )
    : selectedAsset
    ? assetOptions.find((option) => isMatch(selectedAsset as T, option)) || null
    : null;
  // end of selection logic

  const dropdownContentCalculatedWidth = overlayWidthMultiplier
    ? overlayWidthMultiplier * (wrapperRef.current?.offsetWidth || 0)
    : wrapperRef.current?.offsetWidth || 0;

  useEffect(() => {
    if (
      selectedAsset &&
      typeof selectedAsset === 'object' &&
      'key' in selectedAsset
    ) {
      // @ts-ignore
      setSelectedKey(selectedAsset.key);
    } else {
      setSelectedKey(null);
    }
  }, [selectedAsset]);

  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    setIsMobile(window.innerWidth <= 768); // True for all smartphones/tablets
  }, []);

  const handleCreateButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    if (handleCreateClick) handleCreateClick();
  };

  const isSelectedExist = Array.isArray(selectedAsset)
    ? selectedAsset.length > 0
    : (selectedAsset &&
        (selectedAsset.id ?? selectedAsset.key) !== undefined &&
        (selectedAsset.id ?? selectedAsset.key) !== '') ||
      (typeof selectedAsset === 'object' &&
        selectedAsset !== null &&
        Object.keys(selectedAsset as TreeCheckboxSelectionKeys).length > 0 &&
        Object.values(selectedAsset as TreeCheckboxSelectionKeys).every(
          (value) => value && (value.checked || value.partialChecked)
        ));

  const findKeyInTree = useCallback(
    (nodes: TreeNode[], key: string): boolean => {
      for (const node of nodes) {
        if (String(node.key) === key) return true;
        if (node.children && findKeyInTree(node.children, key)) return true;
      }
      return false;
    },
    []
  );

  const deletedWarningUniqueClass = useRef(
    `deleted-dd-${Math.random().toString(36).substr(2, 9)}`
  );

  const hasDeletedSelection = useMemo(() => {
    if (!showDeletedWarning || !isSelectedExist) return false;

    if (useTree) {
      if (!Array.isArray(selectedAsset)) {
        const key = (selectedAsset as any)?.key;
        const treeForDeletedCheck =
          deletedWarningCheckTreeValue &&
          deletedWarningCheckTreeValue.length > 0
            ? deletedWarningCheckTreeValue
            : treeValue;
        return (
          key &&
          String(key).length > 0 &&
          !findKeyInTree(treeForDeletedCheck, String(key))
        );
      }
      return false;
    }

    if (multiplySelectMode && Array.isArray(selectedAsset)) {
      const pool =
        deletedWarningCheckAssetOptions &&
        deletedWarningCheckAssetOptions.length > 0
          ? deletedWarningCheckAssetOptions
          : assetOptions;
      const matchedInPool = pool.filter((option) =>
        selectedAsset.some((selected) => isMatch(selected, option))
      );
      return (
        selectedAsset.length > 0 && matchedInPool.length < selectedAsset.length
      );
    }

    if (Array.isArray(selectedAsset) && Array.isArray(selectedTableItems)) {
      return (
        selectedAsset.length > 0 &&
        (selectedTableItems as T[]).length < selectedAsset.length
      );
    }

    if (!Array.isArray(selectedAsset)) {
      return !selectedTableItems;
    }

    return false;
  }, [
    showDeletedWarning,
    isSelectedExist,
    useTree,
    multiplySelectMode,
    selectedAsset,
    selectedTableItems,
    treeValue,
    deletedWarningCheckTreeValue,
    deletedWarningCheckAssetOptions,
    assetOptions,
    isMatch,
    findKeyInTree
  ]);

  const displayedLabel = () => {
    if (Array.isArray(selectedAsset)) {
      return selectedAsset.length > 0 ? placeholder : 'Выберите значение';
    }
    return isSelectedExist
      ? (selectedAsset as any).label
      : placeholder ?? 'Выберите значение';
  };

  const handleOpenClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (!disabled && op.current) {
      if (isPanelVisible) {
        op.current.hide();
      } else {
        op.current.show(event, event.currentTarget);
      }
    }
  };

  const handleChevronClick = () => {
    if (!disabled) {
      firstButtonRef.current?.click();
    }
  };

  const handleOverlayShow = () => {
    if (firstButtonRef.current && op.current) {
      const buttonRect = firstButtonRef.current.getBoundingClientRect();
      const overlayElement = op.current.getElement();
      const viewportHeight = window.innerHeight;
      const viewportWidth = window.innerWidth;
      const margin = 8;

      (overlayElRef as React.MutableRefObject<HTMLElement | null>).current =
        overlayElement;

      overlayElement!.style.position = 'fixed';

      const overlayHeight = overlayElement!.offsetHeight || 320;
      const overlayWidth =
        overlayElement!.offsetWidth || dropdownContentCalculatedWidth || 420;

      const spaceBelow = viewportHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;

      let top =
        spaceBelow >= overlayHeight || spaceBelow >= spaceAbove
          ? buttonRect.bottom
          : buttonRect.top - overlayHeight;

      top = Math.max(
        margin,
        Math.min(top, viewportHeight - overlayHeight - margin)
      );

      let left = buttonRect.left;
      left = Math.max(
        margin,
        Math.min(left, viewportWidth - overlayWidth - margin)
      );

      overlayElement!.style.top = `${top}px`;
      overlayElement!.style.left = `${left}px`;

      setIsPanelVisible(true);
    }
  };

  useEffect(() => {
    if (isPanelVisible && globalFilterRef.current) {
      globalFilterRef.current.focus();
    }
  }, [isPanelVisible]);

  useClickOutside(overlayElRef, () => {
    if (isPanelVisible && (multiplySelectMode || useTree)) {
      op.current?.hide();
    }
  });

  const calculateScrollHeight = () => {
    if (customHeight !== undefined) {
      return `${customHeight}px`;
    }

    const itemHeight = 47; // Height of each item
    const maxScrollHeight = customHeight ? customHeight : 250; // Maximum scroll height
    const minScrollHeight = 50; // Minimum scroll height when empty

    if (assetOptions.length === 0) {
      return `${minScrollHeight}px`; // Scroll height when no items are present
    }

    const calculatedHeight = assetOptions.length * itemHeight;
    return `${Math.min(
      calculatedHeight + (columns ? 40 : 0),
      maxScrollHeight
    )}px`; // Use the smaller value between calculated height and max height
  };

  const clearSelection = () => {
    if (dialogCancelationWarn) setCancelationWarnVisible(false);
    if (setIsDepartmentDeleted) {
      setIsDepartmentDeleted(true);
    }
    if (onTreeSelectionChange) {
      if (!multiplySelectMode) {
        onTreeSelectionChange({ key: '', label: '' });
      }
      if (multiplySelectMode) {
        onTreeSelectionChange([]);
      }
    }
    if (multiplySelectMode) {
      setSelectedAsset?.([]);
      onSelectionChange?.([]);
    } else if (!multiplySelectMode && !onTreeSelectionChange) {
      setSelectedAsset?.(null);
      onSelectionChange?.(null);
    }
  };

  const onFilterChange = (
    filter: string,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = e.target.value;
    const _filters = JSON.parse(JSON.stringify(filters));
    _filters[filter].value = value;
    setFilters(_filters);
  };

  const globalFilterValue = String(filters?.global?.value ?? '').trim();
  const filteredAssetOptions = React.useMemo(() => {
    if (!globalFilterValue) return assetOptions;

    const query = globalFilterValue.toLocaleLowerCase();
    const fields =
      columns && columns.length > 0
        ? columns.map((c) => String(c.field))
        : [fieldName];

    return assetOptions.filter((item) =>
      fields.some((field) => {
        const raw = (item as any)?.[field];
        if (raw === null || raw === undefined) return false;
        return String(raw).toLocaleLowerCase().includes(query);
      })
    );
  }, [assetOptions, columns, fieldName, globalFilterValue]);

  const isOptionSelected = useCallback(
    (option: T, selection: T[]) => selection.some((s) => isMatch(s, option)),
    [isMatch]
  );

  const mergeTableSelection = useCallback(
    (tableSelection: T[]): T[] => {
      if (!Array.isArray(selectedAsset)) return tableSelection;

      const kept = selectedAsset.filter(
        (item) =>
          !assetOptions.some((option) => isMatch(item, option)) ||
          tableSelection.some((selected) => isMatch(item, selected))
      );

      const added = tableSelection.filter(
        (selected) => !selectedAsset.some((item) => isMatch(item, selected))
      );

      return [...kept, ...added];
    },
    [assetOptions, isMatch, selectedAsset]
  );

  const computeSelectAllState = useCallback((): boolean => {
    if (!multiplySelectMode || !Array.isArray(selectedAsset)) return false;

    const target = selectAllFromFiltered ? filteredAssetOptions : assetOptions;
    if (!target || target.length === 0) return false;
    return target.every((opt) => isOptionSelected(opt, selectedAsset));
  }, [
    assetOptions,
    filteredAssetOptions,
    isOptionSelected,
    multiplySelectMode,
    selectAllFromFiltered,
    selectedAsset
  ]);

  const { debouncedCallback: debouncedOnFilterChange } = useDebounce(
    onFilterChange,
    300
  );

  const handleInputChange = useCallback(
    (filter: string) => (e: React.ChangeEvent<HTMLInputElement>) => {
      debouncedOnFilterChange(filter, e);
    },
    [debouncedOnFilterChange]
  );

  const handleSelectAllChange = () => {
    if (multiplySelectMode) {
      const currentSelection = Array.isArray(selectedAsset)
        ? selectedAsset
        : [];
      const target = selectAllFromFiltered
        ? filteredAssetOptions
        : assetOptions;

      if (!selectAll) {
        // Select all (in scope)
        const next = [...currentSelection];
        target.forEach((opt) => {
          if (!isOptionSelected(opt, next)) next.push(opt);
        });
        setSelectedAsset?.(next);
        onSelectionChange?.(next);
      } else {
        // Deselect all (in scope)
        const next = currentSelection.filter(
          (sel) => !target.some((opt) => isMatch(sel, opt))
        );
        setSelectedAsset?.(next);
        onSelectionChange?.(next);
      }
    }
  };

  useEffect(() => {
    setSelectAll(computeSelectAllState());
  }, [computeSelectAllState]);

  useEffect(() => {
    const handleResize = () => {
      if (isPanelVisible && op.current) {
        op.current.hide();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isPanelVisible]);

  const renderHeader = () => {
    return (
      <div className="flex flex-column">
        <div id="table-header" className="flex justify-content-between">
          <div className="w-full assets-filters flex align-items-center gap-2">
            <IconField iconPosition="left" className="w-full">
              <InputIcon className="pi pi-search" />
              <InputText
                ref={globalFilterRef}
                onChange={handleInputChange('global')}
                className="w-full"
              />
            </IconField>
            {selectAllBtn && multiplySelectMode && (
              <Checkbox
                checked={selectAll}
                onChange={handleSelectAllChange}
                tooltip={
                  selectAllTooltip ||
                  (selectAll ? 'Снять выбор' : 'Выбрать всё')
                }
                tooltipOptions={{ position: 'left' }}
              />
            )}
          </div>
        </div>
      </div>
    );
  };

  const header = renderHeader();

  const handleOverlayHide = () => {
    (overlayElRef as React.MutableRefObject<HTMLElement | null>).current = null;
    setFilters(initialFilters);
    if (globalFilterRef.current) {
      globalFilterRef.current.value = '';
    }
    if (useTree) {
      setTreeExpandedKeys({});
    }
    setIsPanelVisible(false);
  };

  const handleTreeSelect = (
    selectedKey: string | TreeCheckboxSelectionKeys | TreeNode[]
  ) => {
    if (!selectedKey && !allowDeselect && multiplySelectMode) {
      return;
    } else if (!selectedKey && allowDeselect && !multiplySelectMode) {
      onTreeSelectionChange?.({ key: '', label: '' });
    }

    // Helper function to find a node in the tree structure
    const findNodeInTree = (
      nodes: TreeNode[],
      key: string
    ): TreeNode | undefined => {
      for (const node of nodes) {
        if (node.key === key) {
          return node;
        }
        if (node.children) {
          const foundNode = findNodeInTree(node.children, key);
          if (foundNode) {
            return foundNode;
          }
        }
      }
      return undefined;
    };

    if (multiplySelectMode) {
      // Extract selected nodes from the tree
      const selectedNodes = extractSelectedNodes(
        treeValue,
        selectedKey as TreeCheckboxSelectionKeys
      );

      // Filter nodes to include only branches without children
      const filteredNodes = selectedNodes.filter((node) => {
        const fullNode = findNodeInTree(treeValue, node.key);
        return (
          fullNode && (!fullNode.children || fullNode.children.length === 0)
        );
      });

      // Update selection if the filtered nodes differ
      if (
        !selectedAsset ||
        !Array.isArray(selectedAsset) ||
        JSON.stringify(filteredNodes) !== JSON.stringify(selectedAsset)
      ) {
        onTreeSelectionChange?.(filteredNodes);
        setSelectedAsset?.(filteredNodes as unknown as T[]);
      }
    } else {
      // For single selection, find the selected node and ensure it has no children
      const selectedNode = findNodeInTree(treeValue, selectedKey as string);
      if (
        selectedNode &&
        (!selectedNode.children || selectedNode.children.length === 0)
      ) {
        onTreeSelectionChange?.(selectedNode);
        setSelectedAsset?.(selectedNode as unknown as T);
      }
    }
  };

  const cancelHandle = () => {
    if (dialogCancelationWarn) {
      setCancelationWarnVisible(true);
    } else {
      clearSelection();
    }
  };

  return (
    <div
      ref={wrapperRef}
      className={`${
        passedClassName ?? ''
      } custom-dropdown custom-dropdown-wrapper ${disabled ? 'disabled' : ''}`}
      style={{ height: 42, width: '100%' }}
    >
      {/* TODO Вынести диалог относящийся к конкретной части ui либо зарефакторить избавившись от захардкоженных неймингов и передавать их пропсами */}
      <Dialog
        visible={cancelationWarnVisible}
        onHide={() => setCancelationWarnVisible(false)}
        header="Вы уверены?"
        style={{ width: '30vw' }}
        pt={withDialogFooterSpacing()}
        footer={
          <div>
            <Button
              label="Да, сбросить"
              onClick={clearSelection}
              className="p-button-danger"
            />
            <Button
              label="Нет, отмена"
              onClick={() => setCancelationWarnVisible(false)}
              className="p-button-secondary"
            />
          </div>
        }
      >
        <div>
          Все выбранные <b>плановые работы</b> будут сброшены.
          <br /> Данную операцию невозможно отменить.
        </div>
      </Dialog>
      <Toast ref={useRef(null)} />
      <div
        className="custom-dropdown p-dropdown p-component p-inputwrapper p-dropdown-clearable p-inputwrapper-filled w-full"
        style={style}
      >
        <div className="custom-btn">
          <div className="flex flex-row justify-content-between">
            <button
              ref={firstButtonRef}
              type="button"
              onClick={handleOpenClick}
              className={`p-dropdown-label${
                !handleCreateClick ? ' withoutCreate' : ''
              }`}
              title={fixedLabel ?? displayedLabel()}
              disabled={disabled}
            >
              <span
                className="flex align-items-center gap-2 text-base"
                style={{ overflow: 'hidden', width: '100%' }}
              >
                <span
                  style={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {fixedLabel ?? displayedLabel()}
                </span>
                {hasDeletedSelection && (
                  <i
                    className={`pi pi-exclamation-circle flex-shrink-0 ${deletedWarningUniqueClass.current}`}
                    style={{ color: 'rgb(255, 168, 0)', fontSize: '1rem' }}
                    data-pr-tooltip={
                      deletedWarningTooltip || 'Удалено в справочнике'
                    }
                    onClick={(e) => e.stopPropagation()}
                  />
                )}
              </span>
            </button>
            <div className="custom-dropdown-action-buttons">
              {handleCreateClick && (
                <Button
                  type="button"
                  icon="pi pi-plus"
                  severity="success"
                  tooltip={createButtonTooltip}
                  tooltipOptions={{ position: 'top' }}
                  text
                  style={{ width: '2.5rem', height: '2.5rem' }}
                  onClick={handleCreateButtonClick}
                  disabled={disabled} // Disable button if disabled prop is true
                  className={showClearOnHover ? 'create-on-hover' : undefined}
                />
              )}
              {isSelectedExist && (
                <Button
                  key={JSON.stringify(selectedAsset)}
                  icon="pi pi-times"
                  tooltip={clearButtonTooltip}
                  tooltipOptions={{ position: 'top' }}
                  text
                  severity="danger"
                  aria-label="Cancel"
                  style={{ width: '2.5rem', height: '2.5rem' }}
                  onClick={cancelHandle}
                  onBlur={() => setIsPanelVisible(false)} // Add this line
                  disabled={disabled}
                  className={showClearOnHover ? 'clear-on-hover' : undefined}
                />
              )}
              <Button
                text
                type="button"
                severity="secondary"
                icon="pi pi-chevron-down"
                className="p-dropdown-arrow-button"
                onClick={handleChevronClick}
                style={{ width: '2.5rem', height: '2.5rem' }}
                disabled={disabled} // Disable chevron button if disabled prop is true
              />
            </div>
          </div>
        </div>
        {hasDeletedSelection && (
          <Tooltip target={`.${deletedWarningUniqueClass.current}`} />
        )}
        <OverlayPanel
          ref={op}
          showCloseIcon
          dismissable={!multiplySelectMode && !useTree}
          className={
            (columns && assetOptions.length > 0 ? '' : 'withoutHeader ') +
            'custom-overlay-panel-dropdown ' +
            overlayClassName
          }
          onHide={handleOverlayHide}
          onShow={handleOverlayShow}
          style={{
            minWidth: minWidth ? `${minWidth}px` : 'auto',
            width: dropdownContentCalculatedWidth + 'px'
          }}
        >
          <div className="flex align-items-center gap-2">
            {showToggleButton && (
              <div className="flex align-items-center gap-2">
                <Button
                  icon={showAll ? 'pi pi-filter-slash' : 'pi pi-filter'}
                  severity={showAll ? 'info' : undefined}
                  tooltip={
                    selectAllTooltip ||
                    (showAll ? toggleShowFilteredTooltip : toggleShowAllTooltip)
                  }
                  tooltipOptions={{ position: 'top' }}
                  text
                  id="customFilter"
                  onClick={onToggleShowAll}
                  style={{ width: '2.5rem', height: '2.5rem' }}
                  className="customFilter-toggle-btn"
                />
                {!showAll && departmentDropdown && assetId && (
                  <>
                    <i
                      className="pi pi-info-circle departmentDropdown-tooltip"
                      data-pr-tooltip={
                        'Отображаются все участки, подходящие по специализации, географическому\nсоответствию и контрольным срокам с учетом приоритезации по загрузке\nи приоритетам'
                      }
                      data-pr-position="top"
                      style={{ color: 'rgb(59 130 246)', cursor: 'pointer' }}
                    />
                    <Tooltip
                      target=".departmentDropdown-tooltip"
                      style={{ whiteSpace: 'pre-line' }}
                    />
                  </>
                )}
                {filterWithLabel && (
                  <label
                    htmlFor="customFilter"
                    className="font-medium text-color cursor-pointer hover:text-primary transition-colors"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    {selectAllTooltip ||
                      (showAll
                        ? toggleShowFilteredTooltip
                        : toggleShowAllTooltip)}
                  </label>
                )}
              </div>
            )}
          </div>
          {useTree ? (
            treeValue.length === 0 && emptyMessage ? (
              <div
                className="p-3 text-600"
                style={{
                  minHeight: customHeight ? `${customHeight}px` : '120px',
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                {emptyMessage}
              </div>
            ) : (
              <Tree
                style={{ height: customHeight ? `${customHeight}px` : '340px' }}
                value={treeValue}
                expandedKeys={treeExpandedKeys}
                onToggle={(e) => setTreeExpandedKeys(e.value)}
                metaKeySelection={metaKey}
                selectionMode={multiplySelectMode ? 'checkbox' : 'single'}
                selectionKeys={
                  multiplySelectMode
                    ? transformToSelectionKeys(selectedAsset as T[], treeValue)
                    : selectedKey
                }
                onSelectionChange={(e) => handleTreeSelect(e.value as string)}
                filter
                filterMode="strict"
                filterPlaceholder="Поиск по дереву"
              />
            )
          ) : multiplySelectMode ? (
            <DataTable
              dataKey={dataKeyName}
              value={assetOptions}
              showGridlines={!!columns}
              selectionMode="multiple"
              metaKeySelection={false}
              emptyMessage={emptyMessage || undefined}
              onSelectionChange={(e) => {
                const next = mergeTableSelection(e.value as T[]);
                onSelectionChange?.(next);
                setSelectedAsset?.(next);
              }}
              compareSelectionBy="equals"
              selection={(selectedTableItems as T[]) ?? []}
              style={{
                height: customHeight ? `${customHeight + 71}px` : 'auto',
                minWidth: minWidth ? `${minWidth}px` : 'auto',
                width: dropdownContentCalculatedWidth
                  ? `${dropdownContentCalculatedWidth - 14}px`
                  : 'auto'
              }}
              scrollable
              header={header}
              scrollHeight={calculateScrollHeight()}
              filters={filters}
              filterDisplay="menu"
            >
              {isMobile && (
                <Column
                  selectionMode="multiple"
                  headerStyle={{ width: '3rem' }}
                  style={{ width: '3rem', flex: '0 0 3rem' }}
                />
              )}
              {columns && columns.length > 0 ? (
                columns.map((col: any, i) => (
                  <Column
                    key={i}
                    field={col.field}
                    header={col.header}
                    style={{ width: col?.width }}
                    body={
                      col.body ? (rowData: T) => col.body!(rowData) : undefined
                    }
                  />
                ))
              ) : (
                <Column field={fieldName} />
              )}
            </DataTable>
          ) : (
            <DataTable
              dataKey={dataKeyName}
              value={assetOptions}
              showGridlines={!!columns}
              selectionMode="single"
              emptyMessage={emptyMessage || undefined}
              onSelectionChange={(e) => {
                const selectedValue = e.value as T;
                onSelectionChange?.(selectedValue);
                setSelectedAsset?.(selectedValue);
                op.current?.hide();
              }}
              compareSelectionBy="equals"
              selection={(selectedTableItems as T) ?? null}
              style={{
                minWidth: minWidth ? `${minWidth}px` : 'auto',
                width: dropdownContentCalculatedWidth
                  ? `${dropdownContentCalculatedWidth - 14}px`
                  : 'auto'
              }}
              scrollable
              header={header}
              scrollHeight={calculateScrollHeight()}
              filters={filters}
              filterDisplay="row"
            >
              {columns && columns.length > 0 ? (
                columns.map((col: any, i) => (
                  <Column
                    key={i}
                    field={col.field}
                    header={col.header}
                    style={{ width: col?.width }}
                    body={
                      col.body ? (rowData: T) => col.body!(rowData) : undefined
                    }
                  />
                ))
              ) : (
                <Column field={fieldName} />
              )}
            </DataTable>
          )}
        </OverlayPanel>
      </div>
    </div>
  );
};
